import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError, retryDelay, setCsrfToken, shouldRetry } from '@/lib/api';
import { ME_KEY } from '@/lib/queries';
import { createAppRouter } from '@/router';
import './index.css';

function handleUnauthorized(error: Error): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  const { pathname, href } = router.state.location;
  if (pathname === '/login' || pathname.startsWith('/invite/')) return;
  setCsrfToken(null);
  queryClient.setQueryData(ME_KEY, null);
  void router.navigate({ to: '/login', search: { redirect: href } });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleUnauthorized }),
  mutationCache: new MutationCache({ onError: handleUnauthorized }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetry,
      retryDelay,
    },
  },
});
const router = createAppRouter(queryClient);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Light, dark or the system setting; chosen on the Settings page, stored in this browser. */}
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
